import { useState, useEffect, useMemo, useCallback } from 'react';
import { Bug, Lightbulb, Search, Trash2, Loader2, Clock, CheckCircle2, CircleDot, XCircle, AlertCircle, MessageSquare, Monitor, Image, X, RefreshCw, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { ConfirmModal } from './ConfirmModal';
import { DataTable, type ColumnDef } from './DataTable';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES } from '../lib/auditLog';
import { getUserProfile, type UserRole } from '../lib/auth';

interface FeedbackItem {
  id: string;
  user_id: string;
  type: 'bug' | 'feature';
  title: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  error_details: Record<string, unknown> | null;
  page_context: string;
  user_agent: string;
  admin_notes: string;
  screenshot_urls: string[];
  resolved_by: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  user_email: string;
}

interface FeedbackEnvelope {
  rows: FeedbackItem[];
  total: number;
}

/** One message in the thread on a feedback row (GET /api/feedback/:id/comments). */
interface FeedbackComment {
  id: string;
  feedback_id: string;
  author_id: string | null;
  body: string;
  created_at: string;
  author_name: string;
  author_email: string;
  author_role: UserRole | null;
}

const COMMENT_MAX_LENGTH = 4000;

type FilterType = 'all' | 'bug' | 'feature';
// 'unresolved' is a server-side pseudo-status meaning open OR in_progress.
type FilterStatus = 'all' | 'unresolved' | 'open' | 'in_progress' | 'resolved' | 'closed';

export function FeedbackManagement() {
  const { t } = useTranslation('common');
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<FilterType>('all');
  // Default to "unresolved" (open + in progress) so the page lands on every
  // actionable item AND matches what the Manager dashboard's pills count. It
  // used to default to 'open' exactly, which hid in-progress rows and made the
  // dashboard count look wrong (feedback 2c2aacc8). Admins can still switch to
  // all/open/resolved/etc via the dropdown.
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('unresolved');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingNotesId, setEditingNotesId] = useState<string | null>(null);
  const [notesText, setNotesText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; title: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  // The screen is reachable by every role (a technician sees only their own
  // rows — the server scopes that). Admin-only controls are hidden below rather
  // than left to fail with a 403 on tap: PUT/DELETE /api/feedback are still
  // Admin-only on the server, which is the actual enforcement.
  const [role, setRole] = useState<UserRole | null>(null);
  const [myId, setMyId] = useState<string | null>(null);
  const isAdmin = role === 'Admin';
  const isManager = role === 'Manager' || isAdmin;
  // Comment threads, keyed by feedback id and loaded when a row is expanded.
  const [comments, setComments] = useState<Record<string, FeedbackComment[]>>({});
  const [commentsLoading, setCommentsLoading] = useState<string | null>(null);
  const [commentDrafts, setCommentDrafts] = useState<Record<string, string>>({});
  const [postingComment, setPostingComment] = useState<string | null>(null);
  const [commentError, setCommentError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const profile = await getUserProfile();
      if (cancelled) return;
      setRole(profile?.role ?? null);
      setMyId(profile?.id ?? null);
    })();
    return () => { cancelled = true; };
  }, []);

  const loadFeedback = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filterType !== 'all') params.set('type', filterType);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (debouncedSearch) params.set('search', debouncedSearch);
      params.set('sort_by', sortKey);
      params.set('sort_dir', sortDir);
      params.set('limit', String(pageSize));
      params.set('offset', String(page * pageSize));

      const result = await apiClient.get<FeedbackEnvelope>(`/api/feedback?${params.toString()}`);
      setItems(result.rows.map((r) => ({ ...r, screenshot_urls: r.screenshot_urls || [] })));
      setTotalCount(result.total);
    } catch (err) {
      console.error('Error loading feedback:', err);
      setError(t('feedback.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus, debouncedSearch, sortKey, sortDir, page, pageSize, t]);

  useEffect(() => {
    void loadFeedback();
  }, [loadFeedback]);

  const loadComments = useCallback(async (feedbackId: string) => {
    try {
      setCommentsLoading(feedbackId);
      setCommentError(null);
      const rows = await apiClient.get<FeedbackComment[]>(`/api/feedback/${feedbackId}/comments`);
      setComments((prev) => ({ ...prev, [feedbackId]: rows }));
    } catch (err) {
      console.error('Error loading comments:', err);
      setCommentError(t('feedback.commentsFailedToLoad', 'Could not load the conversation.'));
    } finally {
      setCommentsLoading(null);
    }
  }, [t]);

  // Fetch the thread when a row is opened. Threads are cached per row so
  // collapsing and reopening doesn't re-query; posting updates the cache.
  useEffect(() => {
    if (expandedId && !comments[expandedId]) void loadComments(expandedId);
  }, [expandedId, comments, loadComments]);

  const handlePostComment = async (feedbackId: string) => {
    const body = (commentDrafts[feedbackId] ?? '').trim();
    if (!body) return;
    try {
      setPostingComment(feedbackId);
      setCommentError(null);
      const created = await apiClient.post<FeedbackComment>(
        `/api/feedback/${feedbackId}/comments`,
        { body },
      );
      setComments((prev) => ({ ...prev, [feedbackId]: [...(prev[feedbackId] ?? []), created] }));
      setCommentDrafts((prev) => ({ ...prev, [feedbackId]: '' }));
    } catch (err) {
      console.error('Error posting comment:', err);
      setCommentError(t('feedback.commentFailedToPost', 'Could not post your reply. Please try again.'));
    } finally {
      setPostingComment(null);
    }
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
    setPage(0);
  };

  const handleStatusChange = async (id: string, newStatus: FeedbackItem['status']) => {
    try {
      setUpdatingStatus(id);
      const updateData: Record<string, unknown> = { status: newStatus };

      const item = items.find((i) => i.id === id);
      if (newStatus === 'resolved') {
        // Server has the caller's id; pass null and let server stamp on resolve
        // by way of admin_notes channel? We don't have a server-side resolver
        // attribution path, so the client passes its own profile id when known.
        // For now, server PUT accepts resolved_by/resolved_at if provided.
        updateData.resolved_at = new Date().toISOString();
      } else if (newStatus === 'open' || newStatus === 'in_progress') {
        updateData.resolved_by = null;
        updateData.resolved_at = null;
      }

      await apiClient.put(`/api/feedback/${id}`, updateData);

      await logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.FEEDBACK,
        entityId: id,
        entityLabel: item?.title || '',
        details: { status: { old: item?.status, new: newStatus } },
        pageContext: 'feedback-management',
      });
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, ...updateData } as FeedbackItem : it)),
      );
    } catch (err) {
      console.error('Error updating status:', err);
      setError(t('feedback.failedToUpdate'));
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleSaveNotes = async (id: string) => {
    try {
      setSavingNotes(true);
      await apiClient.put(`/api/feedback/${id}`, { admin_notes: notesText });

      const item = items.find((i) => i.id === id);
      await logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.FEEDBACK,
        entityId: id,
        entityLabel: item?.title || '',
        details: { admin_notes: notesText },
        pageContext: 'feedback-management',
      });

      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, admin_notes: notesText } : it)),
      );
      setEditingNotesId(null);
    } catch (err) {
      console.error('Error saving notes:', err);
      setError(t('feedback.failedToSave'));
    } finally {
      setSavingNotes(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      setDeleting(true);
      await apiClient.del(`/api/feedback/${deleteConfirm.id}`);
      await logAudit({
        action: AUDIT_ACTIONS.DELETE,
        entityType: AUDIT_ENTITIES.FEEDBACK,
        entityId: deleteConfirm.id,
        entityLabel: deleteConfirm.title,
        pageContext: 'feedback-management',
      });
      setItems((prev) => prev.filter((it) => it.id !== deleteConfirm.id));
      setTotalCount((prev) => prev - 1);
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting feedback:', err);
      setError(t('feedback.failedToDelete'));
    } finally {
      setDeleting(false);
    }
  };

  const statusIcon = (status: string) => {
    switch (status) {
      case 'open': return <CircleDot size={14} className="text-blue-500" />;
      case 'in_progress': return <Clock size={14} className="text-amber-500" />;
      case 'resolved': return <CheckCircle2 size={14} className="text-green-500" />;
      case 'closed': return <XCircle size={14} className="text-gray-400" />; // theme-allow: neutral closed-status icon (part of status-icon set)
      default: return null;
    }
  };

  const statusLabel = (status: string) => {
    const map: Record<string, string> = {
      open: t('feedback.open'),
      in_progress: t('feedback.inProgress'),
      resolved: t('feedback.resolved'),
      closed: t('feedback.closed'),
    };
    return map[status] || status;
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      open: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
      in_progress: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
      resolved: 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300',
      closed: 'bg-gray-100 text-gray-500', // theme-allow: neutral status pill (part of status color map)
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || ''}`}>
        {statusIcon(status)}
        {statusLabel(status)}
      </span>
    );
  };

  const priorityBadge = (priority: string) => {
    const labels: Record<string, string> = {
      low: t('feedback.priorityLow'),
      medium: t('feedback.priorityMedium'),
      high: t('feedback.priorityHigh'),
      critical: t('feedback.priorityCritical'),
    };
    const colors: Record<string, string> = {
      low: 'bg-gray-100 text-gray-600', // theme-allow: neutral priority pill (part of priority color map)
      medium: 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400',
      high: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300',
      critical: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',
    };
    return (
      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${colors[priority] || ''}`}>
        {labels[priority] || priority}
      </span>
    );
  };

  const bugCount = items.filter((i) => i.type === 'bug').length;
  const featureCount = items.filter((i) => i.type === 'feature').length;

  const columns: ColumnDef<FeedbackItem>[] = useMemo(() => [
    {
      key: 'type',
      label: '',
      headerClassName: 'w-10',
      render: (item) => {
        const done = item.status === 'resolved' || item.status === 'closed';
        return item.type === 'bug'
          ? <Bug size={16} className={done ? 'text-red-300' : 'text-red-500'} />
          : <Lightbulb size={16} className={done ? 'text-teal-300' : 'text-teal-500'} />;
      },
    },
    {
      key: 'id',
      label: t('feedback.bugId'),
      headerClassName: 'hidden lg:table-cell w-24',
      cellClassName: 'hidden lg:table-cell',
      render: (item) => (
        <span className="text-xs font-mono text-theme-secondary" title={item.id}>
          {item.id.slice(0, 8)}
        </span>
      ),
    },
    {
      key: 'title',
      label: t('feedback.title'),
      sortable: false,
      // Cap the cell's natural width so a long title (e.g. "No permite
      // guardar la nueva información de los controles") truncates with an
      // ellipsis instead of expanding the column and forcing a horizontal
      // scrollbar on the whole table. clamp() scales with viewport so the
      // column gets meaningful space on wide monitors but doesn't blow out
      // on tablets.
      render: (item) => {
        const done = item.status === 'resolved' || item.status === 'closed';
        return (
          <div
            className="flex items-center gap-2 overflow-hidden"
            style={{ maxWidth: 'clamp(180px, 32vw, 520px)' }}
            title={item.title}
          >
            <span className={`text-sm font-medium truncate ${done ? 'line-through text-theme-secondary' : 'text-theme-primary'}`}>{item.title}</span>
            {item.screenshot_urls?.length > 0 && (
              <Image size={14} className="text-theme-secondary flex-shrink-0" />
            )}
          </div>
        );
      },
    },
    {
      key: 'user',
      label: t('feedback.user'),
      headerClassName: 'hidden md:table-cell',
      cellClassName: 'hidden md:table-cell',
      render: (item) => {
        const done = item.status === 'resolved' || item.status === 'closed';
        return (
          <span className={`text-xs truncate block max-w-[160px] ${done ? 'text-theme-secondary' : 'text-theme-secondary'}`}>{item.user_email || 'Unknown'}</span>
        );
      },
    },
    {
      key: 'priority',
      label: t('feedback.priority'),
      sortable: true,
      headerClassName: 'hidden sm:table-cell',
      cellClassName: 'hidden sm:table-cell',
      render: (item) => {
        const done = item.status === 'resolved' || item.status === 'closed';
        return done ? (
          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-subtle text-theme-secondary">
            {({ low: t('feedback.priorityLow'), medium: t('feedback.priorityMedium'), high: t('feedback.priorityHigh'), critical: t('feedback.priorityCritical') } as Record<string, string>)[item.priority] || item.priority}
          </span>
        ) : priorityBadge(item.priority);
      },
    },
    {
      key: 'status',
      label: t('feedback.statusCol'),
      sortable: true,
      render: (item) => statusBadge(item.status),
    },
    {
      key: 'created_at',
      label: t('feedback.date'),
      sortable: true,
      headerClassName: 'hidden lg:table-cell',
      cellClassName: 'hidden lg:table-cell',
      render: (item) => {
        const done = item.status === 'resolved' || item.status === 'closed';
        return (
          <span className={`text-xs ${done ? 'text-theme-secondary' : 'text-theme-secondary'}`}>{new Date(item.created_at).toLocaleDateString()}</span>
        );
      },
    },
    // Deleting a report is Admin-only (requireRole('Admin') on the server is the
    // enforcement); nobody else is shown a button that would only 403.
    ...(isAdmin ? [{
      key: 'actions',
      label: '',
      headerClassName: 'w-10',
      render: (item: FeedbackItem) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setDeleteConfirm({ id: item.id, title: item.title });
          }}
          className="p-1.5 text-theme-secondary hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
        >
          <Trash2 size={14} />
        </button>
      ),
    }] : []),
  ], [t, isAdmin]);

  const renderExpandedRow = (item: FeedbackItem) => {
    const isUpdating = updatingStatus === item.id;
    const isEditingNotes = editingNotesId === item.id;
    const thread = comments[item.id];
    const draft = commentDrafts[item.id] ?? '';
    const posting = postingComment === item.id;

    return (
      <div className="bg-subtle rounded-lg border border-line p-4 space-y-4 ml-6">
        <div>
          <p className="text-xs font-medium text-theme-secondary mb-1">{t('feedback.description')}</p>
          <p className="text-sm text-theme-primary whitespace-pre-wrap">{item.description}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div>
            <p className="text-theme-secondary font-medium">{t('feedback.bugId')}</p>
            <p className="text-theme-primary mt-0.5 font-mono text-[11px] select-all">{item.id}</p>
          </div>
          <div>
            <p className="text-theme-secondary font-medium">{t('feedback.submittedBy')}</p>
            <p className="text-theme-primary mt-0.5">{item.user_email || 'Unknown'}</p>
          </div>
          <div>
            <p className="text-theme-secondary font-medium">{t('feedback.page')}</p>
            <p className="text-theme-primary mt-0.5">{item.page_context || t('feedback.na')}</p>
          </div>
          <div>
            <p className="text-theme-secondary font-medium">{t('feedback.created')}</p>
            <p className="text-theme-primary mt-0.5">{new Date(item.created_at).toLocaleString()}</p>
          </div>
          {item.resolved_at && (
            <div>
              <p className="text-theme-secondary font-medium">{t('feedback.resolved')}</p>
              <p className="text-theme-primary mt-0.5">{new Date(item.resolved_at).toLocaleString()}</p>
            </div>
          )}
        </div>

        {item.screenshot_urls?.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Image size={12} className="text-theme-secondary" />
              <p className="text-xs font-medium text-theme-secondary">{t('feedback.screenshots')}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {item.screenshot_urls.map((url, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setLightboxUrl(url); }}
                  className="w-24 h-24 rounded-lg overflow-hidden border border-line hover:border-blue-400 hover:shadow-md transition-all"
                >
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Raw diagnostics are triage material — Manager+ only. A technician
            reading their own report gets the human parts, not a JSON dump. */}
        {item.error_details && isManager && (
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <Monitor size={12} className="text-theme-secondary" />
              <p className="text-xs font-medium text-theme-secondary">{t('feedback.errorDetails')}</p>
            </div>
            <pre className="text-xs bg-gray-900 text-gray-100 rounded-lg p-3 overflow-y-auto max-h-48 whitespace-pre-wrap break-all">{/* theme-allow: intentional dark code/JSON viewer, kept dark in all themes */}
              {JSON.stringify(item.error_details, null, 2)}
            </pre>
          </div>
        )}

        {/* Changing status is Admin-only (PUT /api/feedback/:id). */}
        {isAdmin && (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-xs font-medium text-theme-secondary mr-1">{t('feedback.setStatus')}</p>
          {(['open', 'in_progress', 'resolved', 'closed'] as const).map((s) => (
            <button
              key={s}
              onClick={(e) => { e.stopPropagation(); void handleStatusChange(item.id, s); }}
              disabled={isUpdating || item.status === s}
              className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                item.status === s
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-500/20 dark:border-blue-500/30 dark:text-blue-300'
                  : 'bg-surface border-line text-theme-secondary hover:bg-subtle'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isUpdating ? <Loader2 size={12} className="animate-spin" /> : statusLabel(s)}
            </button>
          ))}
        </div>
        )}

        {/* admin_notes stays exactly as it was: it is the single text column the
            CLI has always appended to, and old rows still carry their history
            there. New back-and-forth goes in the thread below instead. Only an
            Admin can edit it (PUT is Admin-only); everyone who can see the row
            can read it, which is how a reporter sees the answer they were
            already being given. */}
        {(isAdmin || item.admin_notes) && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-medium text-theme-secondary">{t('feedback.adminNotes')}</p>
            {isAdmin && !isEditingNotes && (
              <button
                onClick={(e) => { e.stopPropagation(); setEditingNotesId(item.id); setNotesText(item.admin_notes || ''); }}
                className="text-xs text-blue-600 hover:underline"
              >
                {item.admin_notes ? t('actions.edit') : t('feedback.addNote')}
              </button>
            )}
          </div>
          {isEditingNotes ? (
            <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
              <textarea
                value={notesText}
                onChange={(e) => setNotesText(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder={t('feedback.internalNotesPlaceholder')}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => handleSaveNotes(item.id)}
                  disabled={savingNotes}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                >
                  {savingNotes && <Loader2 size={12} className="animate-spin" />}
                  {t('actions.save')}
                </button>
                <button
                  onClick={() => setEditingNotesId(null)}
                  disabled={savingNotes}
                  className="px-3 py-1.5 bg-surface border border-line text-theme-secondary rounded-md text-xs font-medium hover:bg-subtle transition-colors"
                >
                  {t('actions.cancel')}
                </button>
              </div>
            </div>
          ) : item.admin_notes ? (
            <p className="text-sm text-theme-secondary bg-surface border border-line rounded-lg p-3 whitespace-pre-wrap">{item.admin_notes}</p>
          ) : (
            <p className="text-xs text-theme-secondary italic">{t('feedback.noAdminNotes')}</p>
          )}
        </div>
        )}

        {/* Conversation (FR 0a039c1b). Open to everyone who can see the row, so
            the person who reported the problem can answer a question or add
            something they forgot instead of filing a second report. */}
        <div onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1.5 mb-2">
            <MessageSquare size={12} className="text-theme-secondary" />
            <p className="text-xs font-medium text-theme-secondary">
              {t('feedback.conversation', 'Conversation')}
            </p>
          </div>

          {!thread && commentsLoading === item.id ? (
            <div className="flex items-center gap-2 text-xs text-theme-secondary mb-3">
              <Loader2 size={12} className="animate-spin" />
              {t('feedback.commentsLoading', 'Loading conversation...')}
            </div>
          ) : thread && thread.length > 0 ? (
            <ul className="space-y-2 mb-3">
              {thread.map((c) => {
                const mine = !!c.author_id && c.author_id === myId;
                const staff = c.author_role === 'Admin' || c.author_role === 'Manager';
                return (
                  <li key={c.id} className="bg-surface border border-line rounded-lg p-3">
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <span className="text-xs font-medium text-theme-primary truncate">
                        {mine
                          ? t('feedback.commentAuthorYou', 'You')
                          : c.author_name || c.author_email || t('feedback.commentAuthorUnknown', 'Unknown')}
                        {staff && !mine && (
                          <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300">
                            {t('feedback.commentStaffBadge', 'Team')}
                          </span>
                        )}
                      </span>
                      <span className="text-[11px] text-theme-secondary flex-shrink-0">
                        {new Date(c.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-theme-primary whitespace-pre-wrap break-words">{c.body}</p>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-xs text-theme-secondary italic mb-3">
              {t('feedback.noComments', 'No replies yet.')}
            </p>
          )}

          {commentError && (
            <p className="text-xs text-red-600 dark:text-red-400 mb-2">{commentError}</p>
          )}

          <div className="space-y-2">
            <textarea
              value={draft}
              maxLength={COMMENT_MAX_LENGTH}
              onChange={(e) => setCommentDrafts((prev) => ({ ...prev, [item.id]: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder={t('feedback.commentPlaceholder', 'Add a note or reply to the team...')}
            />
            <div className="flex items-center gap-3">
              <button
                onClick={() => void handlePostComment(item.id)}
                disabled={posting || !draft.trim()}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {posting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                {t('feedback.postComment', 'Post reply')}
              </button>
              <span className="text-[11px] text-theme-secondary">
                {draft.length}/{COMMENT_MAX_LENGTH}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div>
          {/* A technician or manager isn't "managing" anything here — they're
              reading the reports they can see and replying on them. */}
          <h1 className="text-2xl font-bold text-theme-primary">
            {isAdmin ? t('feedback.mgmtTitle') : t('feedback.listTitle', 'Feedback')}
          </h1>
          <p className="text-sm text-theme-secondary mt-1">
            {isAdmin
              ? t('feedback.mgmtSubtitle')
              : t('feedback.listSubtitle', 'Bug reports and feature requests, and the replies on them')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadFeedback}
            disabled={loading}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-line bg-surface hover:bg-subtle transition-colors text-xs font-medium text-theme-secondary disabled:opacity-50"
            title={t('actions.refresh', 'Refresh')}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            {t('actions.refresh', 'Refresh')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-3 flex-shrink-0">
        <div className="bg-surface rounded-lg border border-line px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center dark:bg-blue-500/20">
            <MessageSquare size={18} className="text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-theme-primary">{totalCount}</p>
            <p className="text-xs text-theme-secondary">{t('feedback.total')}</p>
          </div>
        </div>
        <div className="bg-surface rounded-lg border border-line px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center dark:bg-red-500/20">
            <Bug size={18} className="text-red-600 dark:text-red-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-theme-primary">{bugCount}</p>
            <p className="text-xs text-theme-secondary">{t('feedback.bugReports')}</p>
          </div>
        </div>
        <div className="bg-surface rounded-lg border border-line px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center dark:bg-teal-500/20">
            <Lightbulb size={18} className="text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-theme-primary">{featureCount}</p>
            <p className="text-xs text-theme-secondary">{t('feedback.featureRequests')}</p>
          </div>
        </div>
      </div>

      <div className="bg-surface rounded-lg shadow-sm border border-line p-3 mb-3 flex-shrink-0">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-theme-secondary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
              placeholder={t('feedback.searchPlaceholder')}
              className="w-full pl-8 pr-3 py-1.5 text-sm bg-surface text-theme-primary border border-line rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={filterType}
              onChange={(e) => { setFilterType(e.target.value as FilterType); setPage(0); }}
              className="flex-1 sm:flex-none px-2 py-1.5 text-sm bg-surface text-theme-primary border border-line rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">{t('feedback.allTypes')}</option>
              <option value="bug">{t('feedback.bugs')}</option>
              <option value="feature">{t('feedback.features')}</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value as FilterStatus); setPage(0); }}
              className="flex-1 sm:flex-none px-2 py-1.5 text-sm bg-surface text-theme-primary border border-line rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">{t('feedback.allStatuses')}</option>
              <option value="unresolved">{t('feedback.unresolved')}</option>
              <option value="open">{t('feedback.open')}</option>
              <option value="in_progress">{t('feedback.inProgress')}</option>
              <option value="resolved">{t('feedback.resolved')}</option>
              <option value="closed">{t('feedback.closed')}</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="py-4 text-center flex-shrink-0">
          <AlertCircle size={24} className="mx-auto text-red-400 mb-1" />
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          <button onClick={loadFeedback} className="mt-1 text-sm text-blue-600 hover:underline">{t('actions.retry')}</button>
        </div>
      )}

      <DataTable
        data={items}
        columns={columns}
        keyField="id"
        loading={loading}
        emptyIcon={<MessageSquare size={40} className="text-theme-secondary" />}
        emptyTitle={t('feedback.noFeedback')}
        emptyDescription={t('feedback.noFeedbackSub')}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
        serverPagination
        totalCount={totalCount}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
        onRowClick={(item) => setExpandedId(expandedId === item.id ? null : item.id)}
        expandedId={expandedId}
        expandedRow={renderExpandedRow}
        rowClassName={(item) => (item.status === 'resolved' || item.status === 'closed') ? 'opacity-60' : ''}
      />

      <ConfirmModal
        isOpen={!!deleteConfirm}
        title={t('feedback.deleteFeedback')}
        message={t('feedback.deleteConfirm', { title: deleteConfirm?.title })}
        confirmText={t('actions.delete')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm(null)}
        variant="danger"
        isLoading={deleting}
        loadingText={t('feedback.deleting')}
      />

      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
          >
            <X size={20} />
          </button>
          <img
            src={lightboxUrl}
            alt=""
            className="max-w-full max-h-[90vh] rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
